import {Injectable} from '@angular/core';
import {AbstractControl, FormGroup, NonNullableFormBuilder} from '@angular/forms';
import {Project} from 'src/app/core/model/project';
import {Tracker, TrackerMetric} from 'src/app/core/model/tracker';
import {MetricNumericType, TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';

@Injectable({
  providedIn: 'root',
})
export class TrackersService {
  constructor(private formBuilder: NonNullableFormBuilder) {}

  getTrackerForm(tracker?: Tracker): FormGroup {
    return this.formBuilder.group({
      name: [tracker?.name || ''],
      projectIds: this.formBuilder.array(
        tracker?.projectIds ? tracker.projectIds.map(projectId => this.formBuilder.control(projectId)) : []
      ),
    });
  }

  getProjectsFormGroup(project?: Partial<Project>): AbstractControl {
    return this.formBuilder.group({
      _id: [project?._id || ''],
      name: [project?.label || ''],
      color: [project?.color || ''],
    });
  }

  getMetricsFormGroup(metric?: TrackerMetric): FormGroup {
    return this.formBuilder.group({
      name: [metric?.name || ''],
      type: [metric?.type || TrackerMetricTypes.Numeric],
      uom: [metric?.uom || ''],
      min: [metric?.min || ''],
      max: [metric?.max || ''],
      options: this.formBuilder.array(
        metric?.options
          ? metric.options.map(optionItem => this.getOptionsFormGroup(optionItem))
          : [
              this.formBuilder.group({
                option: [''],
                value: [1],
              }),
            ]
      ),
      updateable: [metric?.updateable === undefined ? true : metric?.updateable],
      defaultValue: [metric?.defaultValue || ''],
      value: [metric?.value || ''],
      selectedValue: [metric?.selectedValue || null],
      selectedOption: [metric?.selectedOption || ''],
      valueType: [this.getValueTypeForMetricType(metric?.type, metric?.valueType)],
      tally: [metric?.tally || 0],
      numericType: [metric?.numericType || MetricNumericType.Basic],
      durationType: [metric?.durationType || 'hrs/min'],
    });
  }

  getOptionsFormGroup(optionData?: {option: string; value: number}) {
    return this.formBuilder.group({
      option: [optionData?.option || ''],
      value: [optionData?.value || 0],
    });
  }

  isMetricFormValid(metric: TrackerMetric): boolean {
    if (!metric.name) {
      return false;
    }

    if (metric.type === TrackerMetricTypes.Select) {
      return this.isSelectMetricValid(metric);
    }

    if (metric.type === TrackerMetricTypes.Numeric) {
      return this.isNumericMetricValid(metric);
    }

    return true;
  }

  private isSelectMetricValid(metric: TrackerMetric): boolean {
    return !!(metric.options?.length > 0 && metric.options.some(optionItem => !!optionItem.option));
  }

  private isNumericMetricValid(metric: TrackerMetric): boolean {
    if (this.isMinMaxRequired(metric.numericType)) {
      return this.hasValidMinMax(metric);
    }
    return true;
  }

  private isMinMaxRequired(numericType: MetricNumericType): boolean {
    return numericType === MetricNumericType.Stepper || numericType === MetricNumericType.Range;
  }

  private hasValidMinMax(metric: TrackerMetric): boolean {
    const hasMin = metric.min !== null && metric.min !== undefined && metric.min !== '';
    const hasMax = metric.max !== null && metric.max !== undefined && metric.max !== '';

    if (!hasMin || !hasMax) {
      return false;
    }

    return Number(metric.min) <= Number(metric.max);
  }

  formatMetricDto(metricForm: FormGroup): TrackerMetric {
    const formMetric = metricForm.value;
    const defaultValue = formMetric.defaultValue;
    let options: {option: string; value: number}[];
    if (formMetric.type === TrackerMetricTypes.Select) {
      options = formMetric.options
        .filter(optionItem => optionItem.option)
        .map(optionItem => ({
          option: optionItem.option,
          // If type is numeric, both values are the same
          value: formMetric.valueType === 'number' ? +optionItem.option : optionItem.value,
        }));
    } else {
      options = [];
    }

    return {
      ...formMetric,
      options,
      defaultValue,
      value: formMetric.value,
      selectedValue: formMetric.selectedValue,
      selectedOption: formMetric.selectedOption,
    };
  }

  private readonly metricValueTypeMap: Record<TrackerMetricTypes, string> = {
    [TrackerMetricTypes.Numeric]: 'number',
    [TrackerMetricTypes.Boolean]: 'string',
    [TrackerMetricTypes.Select]: 'string',
    [TrackerMetricTypes.Text]: 'string',
    [TrackerMetricTypes.Photo]: 'string',
    [TrackerMetricTypes.Date]: 'string',
    [TrackerMetricTypes.Duration]: 'number',
    [TrackerMetricTypes.Percentage]: 'number',
  };

  getValueTypeForMetricType(type?: TrackerMetricTypes, existingValueType?: string): string {
    if (type === TrackerMetricTypes.Select && existingValueType) {
      return existingValueType;
    }
    return type ? this.metricValueTypeMap[type] : 'number';
  }
}
